import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { TablePagination } from '@/components/common/TablePagination';
import AccreditationReviewPanel from '@/components/customers/AccreditationReviewPanel';
import { TCustomer } from '@/components/customers/CustomersTableColumns';
import {
  InvestmentsTableColumns,
  TInvestment,
} from '@/components/investments/InvestmentsTableColumns';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type LocationState = {
  customer?: TCustomer;
};

const CustomerInvestments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams();
  const state = location.state as LocationState | null;

  const customer = state?.customer;

  const rows: TInvestment[] = useMemo(() => {
    if (!customer || !customer.investments) return [];

    return customer.investments.map((inv) => ({
      id: inv.id,
      customer: {
        id: customer.id,
        name: customer.name,
        email: customer.email,
      },
      asset: inv.asset
        ? {
            assetId: inv.asset.assetId,
            name: inv.asset.name,
            type: inv.asset.type,
          }
        : {
            assetId: '',
            name: '-',
            type: '',
          },
      totalAmount: inv.totalAmount,
      status: inv.status,
      isCrypto: inv.isCrypto,
      createdAt: inv.createdAt,
    }));
  }, [customer]);

  const table = useReactTable({
    data: rows,
    columns: InvestmentsTableColumns as ColumnDef<TInvestment, unknown>[],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  const handleBack = () => {
    navigate('/admin/customers');
  };

  if (!customer) {
    return (
      <div className="px-4 py-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Customers
        </Button>
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6">
          <h2 className="text-lg font-semibold text-destructive">
            No customer data
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            This page is intended to be opened from the customers list. Please
            go back and select a customer again.
          </p>
        </div>
        {customerId && (
          <AccreditationReviewPanel
            customerId={customerId}
            customerName={customerId}
          />
        )}
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">
              {customer.name || 'Customer'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {customer.email}{' '}
              {customer.ironcustomerid && `• ${customer.ironcustomerid}`}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Total Investments
          </p>
          <p className="mt-2 text-2xl font-semibold">{rows.length}</p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Active Wallets
          </p>
          <p className="mt-2 text-2xl font-semibold">
            {customer.wallets?.filter((w) => w.isActive).length ?? 0}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Registered
          </p>
          <p className="mt-2 text-sm">
            {customer.createdat
              ? new Date(customer.createdat).toLocaleString()
              : '-'}
          </p>
        </div>
      </div>

      <AccreditationReviewPanel
        customerId={customer.id}
        customerName={customer.name || customer.email}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Investments</h2>
            <p className="text-sm text-muted-foreground">
              All investments associated with this customer.
            </p>
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getAllColumns().length}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    No investments found for this customer.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <TablePagination table={table} />
      </div>
    </div>
  );
};

export default CustomerInvestments;
